import React, { useCallback, useEffect, useState } from 'react';

import { Alert, Box, Button, TextField } from '@mui/material';
import Spinner from '@provider-portal/common/components/Spinner/Spinner';

import { NetworkRequestState } from '@bolteu/redux-session';

interface ForgotPasswordFormProps {
  strings: Record<'error' | 'emailSend' | 'username' | 'resetPassword', string>;
  onResetPassword: (email: string) => void;
  alertMessage: string | null;
  requestState: NetworkRequestState;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  strings,
  onResetPassword,
  alertMessage,
  requestState,
}) => {
  const [username, setUsername] = useState('');
  const [isErrorEmail, setIsErrorEmail] = useState(false);
  const [helperText, setHelperText] = useState('');
  const [shouldShowErrorAlert, setShouldShowErrorAlert] = useState(false);
  const [shouldShowSuccessAlert, setShouldShowSuccessAlert] = useState(false);

  const resetFormFieldErrors = useCallback(() => {
    setIsErrorEmail(false);
    setShouldShowErrorAlert(false);
    setHelperText('');
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      onResetPassword(username);
    },
    [onResetPassword, username],
  );

  const handleUsernameChange = useCallback(
    (e) => {
      setUsername(e.target.value);
      resetFormFieldErrors();
    },
    [resetFormFieldErrors],
  );

  useEffect(() => {
    if (alertMessage) {
      setHelperText(alertMessage);
      setIsErrorEmail(true);
      return;
    }

    if (requestState === NetworkRequestState.failure) {
      setShouldShowErrorAlert(true);
      return;
    }

    if (requestState === NetworkRequestState.success) {
      setShouldShowSuccessAlert(true);
    }

    resetFormFieldErrors();
  }, [alertMessage, requestState, resetFormFieldErrors]);

  return (
    <form onSubmit={handleSubmit} data-testid="forgot-password-form">
      {shouldShowErrorAlert ? (
        <Alert variant="filled" severity="error">
          {strings.error}
        </Alert>
      ) : null}
      {shouldShowSuccessAlert ? (
        <Alert variant="filled" severity="info">
          {strings.emailSend}
        </Alert>
      ) : null}
      <TextField
        variant="outlined"
        margin="normal"
        fullWidth
        id="username"
        label={strings.username}
        name="username"
        autoComplete="username"
        autoFocus
        onChange={handleUsernameChange}
        error={isErrorEmail}
        helperText={helperText}
      />
      <Box mt={2}>
        <Button type="submit" fullWidth variant="contained" color="primary">
          {requestState === NetworkRequestState.waiting ? <Spinner /> : strings.resetPassword}
        </Button>
      </Box>
    </form>
  );
};

export default ForgotPasswordForm;
